import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X, Inbox } from "lucide-react";
import { toast } from "sonner";
import { playDing } from "@/lib/notifySound";
import { fetchNotifications, markNotificationsRead, relativeTime } from "@/lib/notifications";

// Campana con buzón de avisos/tareas. Suena y muestra una barrita cuando llega algo nuevo
// mientras la app está abierta (sondeo cada 25s + al enfocar). No requiere permisos.
// Si recibe `parentToken`, consulta en el contexto del tutor/alumno.
export default function NotificationCenter({ parentToken = null, tone = "light", floating = false }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const topTsRef = useRef(null);
  const initedRef = useRef(false);

  const go = (url, tag) => {
    // Mensaje de alumno/padre: abre el chat directo con ese alumno.
    const sid = (tag || "").startsWith("stmsg-") ? tag.slice(6) : new URL(url || "/", window.location.origin).searchParams.get("chat");
    if (sid && window.location.pathname.startsWith("/docente")) {
      setOpen(false);
      window.dispatchEvent(new CustomEvent("uai-open-student-chat", { detail: sid }));
      return;
    }
    if (url) window.location.assign(url);
  };

  const load = useCallback(async (silent = true) => {
    try {
      const data = await fetchNotifications(parentToken);
      const list = data.items || [];
      setItems(list);
      setUnread(data.unread || 0);
      window.dispatchEvent(new CustomEvent("zv-unread", { detail: { key: "notif", n: data.unread || 0 } }));
      const top = list[0]?.created_at || null;
      if (silent && initedRef.current && top && top !== topTsRef.current) {
        const fresh = list.filter((it) => !topTsRef.current || (it.created_at || "") > topTsRef.current);
        if (fresh.length) {
          try { playDing(); } catch { /* noop */ }
          const n = fresh[0];
          toast(n.title || "Nuevo aviso", {
            description: n.body || "",
            duration: 8000,
            action: n.url ? { label: "Ver", onClick: () => go(n.url, n.tag) } : undefined,
          });
        }
      }
      topTsRef.current = top;
      initedRef.current = true;
    } catch { /* silencioso */ }
  }, [parentToken]);

  useEffect(() => {
    load(false);
    const t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 25000);
    const onFocus = () => { if (document.visibilityState === "visible") load(true); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load]);

  const openPanel = async () => {
    setOpen(true);
    if (unread > 0) {
      try { await markNotificationsRead(parentToken); setUnread(0); } catch { /* noop */ }
    }
  };

  const iconColor = tone === "dark" ? "text-white" : "text-navy";
  const hoverBg = tone === "dark" ? "hover:bg-white/15" : "hover:bg-nardo-100";

  const triggerClass = floating
    ? "zv-fab-item fixed right-4 bottom-[320px] md:bottom-[226px] z-[60] w-14 h-14 rounded-full shadow-2xl bg-white text-navy border border-navy/20 shadow-navy/20 hover:scale-105 active:scale-95"
    : `relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${iconColor} ${hoverBg}`;
  const badgeClass = floating
    ? "absolute -top-1 -right-1 min-w-[20px] h-5 px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold leading-none border-2 border-white"
    : "absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none border border-white";

  return (
    <>
      <button
        type="button"
        data-testid="notif-bell-btn"
        onClick={openPanel}
        title="Avisos y tareas"
        className={`inline-flex items-center justify-center transition-transform ${triggerClass}`}
      >
        <Bell className={floating ? "w-6 h-6" : "w-5 h-5"} strokeWidth={1.9} />
        {unread > 0 && (
          <span data-testid="notif-badge" className={badgeClass}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[90]" data-testid="notif-overlay">
          <div className="absolute inset-0 bg-black/20" onClick={() => setOpen(false)} />
          <div className="absolute right-2 top-14 md:top-16 w-[94vw] max-w-sm max-h-[70vh] overflow-hidden rounded-2xl bg-white shadow-2xl border border-nardo-200 flex flex-col animate-slide-up" data-testid="notif-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-nardo-100 shrink-0">
              <div className="font-display font-bold text-navy inline-flex items-center gap-2">
                <Bell className="w-4 h-4 text-navy" /> Avisos y tareas
              </div>
              <button onClick={() => setOpen(false)} data-testid="notif-close" className="text-nardo-400 hover:text-navy p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-10 text-center text-nardo-400 text-sm flex flex-col items-center gap-2" data-testid="notif-empty">
                  <Inbox className="w-8 h-8 text-nardo-300" strokeWidth={1.4} />
                  Aún no hay avisos.
                </div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    data-testid="notif-item"
                    onClick={() => go(n.url, n.tag)}
                    className="w-full text-left px-4 py-3 border-b border-nardo-50 hover:bg-nardo-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-navy leading-snug">{n.title}</div>
                      <div className="text-[10px] text-nardo-400 shrink-0 mt-0.5 whitespace-nowrap">{relativeTime(n.created_at)}</div>
                    </div>
                    {n.body && <div className="text-[12px] text-nardo-600 mt-0.5 leading-snug">{n.body}</div>}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
